
import React, { useState, useCallback } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSwipeNavigation } from '@/hooks/useSwipeGestures';
import { useScheduleConflicts } from '@/hooks/useScheduleConflicts';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { cn } from '@/lib/utils';
import ScheduleCard from '../ScheduleCard';
import { ScheduleViewProps } from '../types/scheduleTypes';
import { ScheduleService } from '@/services/classService';
import { toast } from 'sonner';
import AddScheduleDialog from '../AddScheduleDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WeeklyScheduleView: React.FC<ScheduleViewProps> = ({ 
  schedules, 
  classes,
  onScheduleUpdated,
  onScheduleDeleted,
  initialClassId
}) => {
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [quickCreateDialog, setQuickCreateDialog] = useState<{
    open: boolean;
    dayIndex: number;
    timeSlot?: string;
  }>({ open: false, dayIndex: 0 });
  const [draggedSchedule, setDraggedSchedule] = useState<string | null>(null);
  
  // Get organization ID from first schedule or class
  const organizationId = schedules[0]?.organization_id || classes[0]?.organization_id || '';
  
  // Detect schedule conflicts
  const {
    conflicts,
    conflictSummary,
    getConflictsForSchedule,
    getScheduleConflictSeverity,
    getConflictMessage
  } = useScheduleConflicts(schedules);
  
  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const navigateToPreviousWeek = useCallback(() => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  }, []);
  
  const navigateToNextWeek = useCallback(() => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  }, []);
  
  const goToCurrentWeek = useCallback(() => {
    setCurrentWeek(new Date());
  }, []);
  
  
  // Handle clicking empty calendar cells to create schedules
  const handleCellClick = useCallback((dayIndex: number, timeSlot?: string) => {
    setQuickCreateDialog({ 
      open: true, 
      dayIndex, 
      timeSlot 
    });
  }, []);
  
  // Handle schedule creation completion
  const handleScheduleAdded = useCallback(async () => {
    setQuickCreateDialog({ open: false, dayIndex: 0 });
    await onScheduleUpdated();
  }, [onScheduleUpdated]);

  // Drag and drop handlers following React patterns
  const handleDragStart = useCallback((e: React.DragEvent, scheduleId: string) => {
    e.stopPropagation();
    setDraggedSchedule(scheduleId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', scheduleId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedSchedule(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); // Required to allow dropping
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetDayIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    const scheduleId = e.dataTransfer.getData('text/plain');
    if (!scheduleId || !draggedSchedule) return;

    try {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (!schedule) return;

      // Update schedule weekday
      await ScheduleService.updateSchedule(scheduleId, {
        ...schedule,
        weekday: targetDayIndex
      });

      toast.success('Schedule moved successfully');
      await onScheduleUpdated();
    } catch (error) {
      console.error('Error moving schedule:', error);
      toast.error('Failed to move schedule');
    } finally {
      setDraggedSchedule(null);
    }
  }, [schedules, draggedSchedule, onScheduleUpdated]);

  // Simple implementation of makeTimeSlotDropZone function
  const makeTimeSlotDropZone = useCallback((
    element: HTMLElement, 
    dayIndex: number, 
    timeSlot: string, 
    onDropCallback?: () => void
  ) => {
    if (!element) return;

    const handleDragOverWrapper = (e: DragEvent) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'move';
      element.classList.add('bg-blue-50', 'border-blue-300');
    };

    const handleDragLeaveWrapper = () => {
      element.classList.remove('bg-blue-50', 'border-blue-300');
    };

    const handleDropWrapper = async (e: DragEvent) => {
      e.preventDefault();
      element.classList.remove('bg-blue-50', 'border-blue-300');
      
      const scheduleId = e.dataTransfer!.getData('text/plain');
      if (scheduleId) {
        await handleDrop(e as any, dayIndex);
        onDropCallback?.();
      }
    };

    // Add event listeners
    element.addEventListener('dragover', handleDragOverWrapper);
    element.addEventListener('dragleave', handleDragLeaveWrapper);
    element.addEventListener('drop', handleDropWrapper);

    // Return cleanup function
    return () => {
      element.removeEventListener('dragover', handleDragOverWrapper);
      element.removeEventListener('dragleave', handleDragLeaveWrapper);
      element.removeEventListener('drop', handleDropWrapper);
    };
  }, [handleDrop]);

  
  // Keyboard shortcuts for week navigation
  useKeyboardShortcuts([
    {
      key: 'ArrowLeft',
      description: 'Previous week',
      action: navigateToPreviousWeek
    },
    {
      key: 'ArrowRight',
      description: 'Next week', 
      action: navigateToNextWeek
    },
    {
      key: 't',
      description: 'Go to current week',
      action: goToCurrentWeek
    }
  ]);
  
  // Setup swipe gestures for mobile navigation
  const swipeRef = useSwipeNavigation(navigateToPreviousWeek, navigateToNextWeek);
  
  // Get the dates for the current week
  const weekStart = startOfWeek(currentWeek);
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  // Helper function to find a class by ID
  const findClass = (classId: string) => {
    return classes.find(c => c.id === classId);
  };
  
  // Group schedules by weekday
  const schedulesByDay = WEEKDAYS.map((day, index) => {
    const daySchedules = schedules
      .filter(schedule => schedule.weekday === index)
      .sort((a, b) => {
        if (!a.start_time) return 1;
        if (!b.start_time) return -1;
        return a.start_time.localeCompare(b.start_time);
      });
    
    return {
      day,
      dayIndex: index,
      schedules: daySchedules
    };
  });

  return (
    <TooltipProvider>
      <div className="space-y-4 sm:space-y-6" ref={swipeRef}>
        {/* Week Navigation Header - Enhanced Mobile */}
        <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-gray-200/60">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={navigateToPreviousWeek}
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Previous week (← or swipe right)</p>
              </TooltipContent>
            </Tooltip>
            
            <div className="text-center">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Week of {format(weekStart, 'MMMM d')}
              </p>
            </div>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={navigateToNextWeek}
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Next week (→ or swipe left)</p>
              </TooltipContent>
            </Tooltip>
          </div>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={goToCurrentWeek}
                variant="outline"
                size="sm"
                className="min-h-[44px] touch-manipulation"
              >
                <Calendar className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Today</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Go to current week (T)</p>
            </TooltipContent>
          </Tooltip>
        </div>
        
        {/* Schedule Grid - Enhanced Mobile Layout */}
        <div className={cn(
          "bg-white/40 backdrop-blur-sm rounded-xl border border-gray-200/60 overflow-hidden",
          isMobile ? "" : "shadow-lg"
        )}>
          {isMobile ? (
            // Mobile: Vertical stack with horizontal scroll for each day
            <div className="divide-y divide-gray-200/60">
              {schedulesByDay.map(({ day, dayIndex, schedules }) => {
                const currentDate = weekDates[dayIndex];
                const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                
                return (
                  <div key={dayIndex} className="p-4">
                    <div className={cn(
                      "flex items-center justify-between mb-3 pb-2",
                      isToday ? "border-b-2 border-blue-500" : "border-b border-gray-200"
                    )}>
                      <div>
                        <h3 className={cn(
                          "font-semibold text-base",
                          isToday ? "text-blue-700" : "text-gray-900"
                        )}>
                          {day}
                        </h3>
                        <p className={cn(
                          "text-sm",
                          isToday ? "text-blue-600" : "text-gray-600"
                        )}>
                          {format(currentDate, 'MMM d')}
                        </p>
                      </div>
                      {isToday && (
                        <div className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          Today
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-3 min-h-[120px]">
                      {schedules.length > 0 ? (
                        schedules.map(schedule => (
                          <ScheduleCard
                            key={schedule.id}
                            schedule={schedule}
                            classData={findClass(schedule.class_id || '')}
                            onScheduleUpdated={onScheduleUpdated}
                            onScheduleDeleted={onScheduleDeleted}
                            conflicts={getConflictsForSchedule(schedule.id)}
                            conflictSeverity={getScheduleConflictSeverity(schedule.id)}
                            conflictMessage={getConflictMessage(schedule.id)}
                            isDraggable={true}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                          />
                        ))
                      ) : (
                        <button 
                          onClick={() => handleCellClick(dayIndex)}
                          className="min-h-[60px] w-full flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer touch-manipulation group"
                        >
                          <div className="text-center">
                            <p className="text-sm text-gray-500 group-hover:text-blue-600">No classes scheduled</p>
                            <p className="text-xs text-gray-400 group-hover:text-blue-500 mt-1">Click to add</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop: Original grid layout enhanced
            <div className="grid grid-cols-7 gap-0 divide-x divide-gray-200/60">
              {schedulesByDay.map(({ day, dayIndex, schedules }) => {
                const currentDate = weekDates[dayIndex];
                const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                
                return (
                  <div key={dayIndex} className="min-h-[400px] flex flex-col">
                    <div className={cn(
                      "p-4 border-b border-gray-200/60 text-center",
                      isToday ? "bg-blue-50" : "bg-gray-50/50"
                    )}>
                      <h3 className={cn(
                        "font-semibold text-sm mb-1",
                        isToday ? "text-blue-700" : "text-gray-900"
                      )}>
                        {WEEKDAYS_SHORT[dayIndex]}
                      </h3>
                      <p className={cn(
                        "text-lg font-bold",
                        isToday ? "text-blue-700" : "text-gray-700"
                      )}>
                        {format(currentDate, 'd')}
                      </p>
                      <p className={cn(
                        "text-xs",
                        isToday ? "text-blue-600" : "text-gray-500"
                      )}>
                        {format(currentDate, 'MMM')}
                      </p>
                    </div>
                    
                    <div 
                      className="flex-1 p-3 space-y-2 overflow-y-auto drop-zone calendar-drop-zone"
                      ref={(el) => {
                        if (el) {
                          makeTimeSlotDropZone(el, dayIndex, '09:00', () => {
                            // Optional callback when drop succeeds
                          });
                        }
                      }}
                    >
                      {schedules.length > 0 ? (
                        schedules.map(schedule => (
                          <ScheduleCard
                            key={schedule.id}
                            schedule={schedule}
                            classData={findClass(schedule.class_id || '')}
                            onScheduleUpdated={onScheduleUpdated}
                            onScheduleDeleted={onScheduleDeleted}
                            conflicts={getConflictsForSchedule(schedule.id)}
                            conflictSeverity={getScheduleConflictSeverity(schedule.id)}
                            conflictMessage={getConflictMessage(schedule.id)}
                            isDraggable={true}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                          />
                        ))
                      ) : (
                        <button 
                          onClick={() => handleCellClick(dayIndex)}
                          className="h-20 w-full flex items-center justify-center border-2 border-dashed border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group rounded"
                        >
                          <div className="text-center">
                            <p className="text-xs text-gray-400 group-hover:text-blue-600">No classes</p>
                            <p className="text-xs text-gray-300 group-hover:text-blue-500">Click to add</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Mobile Swipe Hint */}
        {isMobile && (
          <div className="text-center py-2">
            <p className="text-xs text-gray-500">
              ← Swipe to navigate between weeks →
            </p>
          </div>
        )}
        
        {/* Quick Create Schedule Dialog */}
        <AddScheduleDialog
          open={quickCreateDialog.open}
          onClose={() => setQuickCreateDialog({ open: false, dayIndex: 0 })}
          organizationId={organizationId}
          classId={''}
          classes={classes}
          onScheduleAdded={handleScheduleAdded}
          initialClassId={initialClassId}
        />
      </div>
    </TooltipProvider>
  );
};

// Export the component with display name for debugging
WeeklyScheduleView.displayName = 'WeeklyScheduleView';

export default WeeklyScheduleView;
